import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchPastEvents, pastEventPdfUrl, type ConferencePastEvent } from "./api";

function localizedTitle(item: ConferencePastEvent, locale: string) {
  if (locale === "ar") return item.title_ar;
  if (locale === "fr") return item.title_fr;
  return item.title_en;
}

function localizedDescription(item: ConferencePastEvent, locale: string) {
  if (locale === "ar") return item.description_ar;
  if (locale === "fr") return item.description_fr;
  return item.description_en;
}

export function PastEventsPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferencePastEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPastEvents()
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t.common.error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t.common.error]);

  return (
    <div>
      <header className="dn-page-header" style={{ marginBottom: "1.5rem" }}>
        <h1>{t.pastEvents.title}</h1>
        <p className="dn-muted">{t.pastEvents.lead}</p>
      </header>

      {loading && <p className="dn-muted">{t.common.loading}</p>}
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div className="dn-card">
          <h3>{t.pastEvents.emptyTitle}</h3>
          <p className="dn-muted">{t.pastEvents.emptyBody}</p>
        </div>
      )}

      <div className="dn-grid">
        {rows.map((item) => {
          const title = localizedTitle(item, locale);
          const description = localizedDescription(item, locale);
          return (
            <article className="dn-card" key={item.id}>
              <div className="dn-paper-card-top">
                <h3>{title}</h3>
                {item.year != null && (
                  <p className="dn-muted">
                    {t.pastEvents.year}: {item.year}
                  </p>
                )}
              </div>
              {description && <p>{description}</p>}
              <div className="dn-cta-row">
                <Link className="dn-btn dn-btn-primary" to={`/past-events/${item.slug}`}>
                  {t.pastEvents.view}
                </Link>
                {item.has_pdf && (
                  <a className="dn-btn dn-btn-ghost" href={pastEventPdfUrl(item.slug, true)}>
                    {t.pastEvents.downloadPdf}
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
